import React, { useEffect, useState } from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import Api from '../Auth/Api';

const styles = StyleSheet.create({
    page: {
        padding: 30,
        backgroundColor: '#E4E4E4'
    },
    title: {
        fontSize: 24,
        textAlign: 'center',
        marginBottom: 20
    },
    subTitle: {
        fontSize: 16,
        textAlign: 'center',
        marginBottom: 20
    },
    question: {
        fontSize: 18,
        margin: 10,
        padding: 10
    },
    question_label: {
        fontSize: 14,
        fontWeight: 'bold'
    },
    answer: {
        fontSize: 16,
        marginLeft: 20,
        marginTop: 5
    },
    correctAnswer: {
        color: 'green'
    },
    incorrectAnswer: {
        color: 'red'
    },
    questionContainer: {
        marginBottom: 50,
        marginTop: 10,
        padding: 20,
        backgroundColor: '#fff',
        borderRadius: 8,
        shadowColor: '#000',
        shadowOpacity: 0.1,
        shadowRadius: 5,
        shadowOffset: { width: 0, height: 2 },
    },
    questionLabel: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#333',
        marginBottom: 15,
    },
    alternativeContainer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 10,
        marginHorizontal: 10,
    },
    alternativeLabel: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#555',
        marginRight: 8,
    },
    alternativeText: {
        fontSize: 12,
        color: '#555',
        flexShrink: 1,
    }
});

const GerarPDF = ({ data, titulo, tipo }) => {

    const token = JSON.parse(localStorage.getItem("user_token"));
    const [progressoAvaliacaoExtra, setProgressoAvaliacaoExtra] = useState(null);
    const [progressoAvaliacaoNormal, setProgressoAvaliacaoNormal] = useState(null);

    const [questoes, setQuestoes] = useState({});
    const [respostasUsuario, setRespostasUsuario] = useState({});

    useEffect(() => {
        async function fetchDataAvaliacaoExtra(id) {
            try {
                const response = await Api.get(`api/info-progresso-avaliacao-extra/${id}`, { headers: { Authorization: `Bearer ${token}` } });
                setProgressoAvaliacaoExtra(response.data);
            } catch (error) {
                console.error("Erro ao buscar avaliação extra:", error.response.data.message);
            }
        }

        async function fetchDataAvaliacaoNormal(id) {
            try {
                const response = await Api.get(`api/info-avaliacao/${id}`, { headers: { Authorization: `Bearer ${token}` } });
                setProgressoAvaliacaoNormal(response.data);
            } catch (error) {
                console.error("Erro ao buscar avaliação normal:", error.response.data.message);
            }
        }

        if (tipo === 'avaliacao_extra') fetchDataAvaliacaoExtra(data);
        if (tipo === 'avaliacao_normal_visualizacao' || tipo === 'avaliacao_completa_visualizacao') fetchDataAvaliacaoNormal(data);
    }, [token, tipo, data]);

    useEffect(() => {
        if (tipo === "avaliacao_extra" && progressoAvaliacaoExtra) {
            const avaliacaoData = progressoAvaliacaoExtra;
            const questoesParsed = JSON.parse(avaliacaoData?.questoes ?? '{}');
            const respostasParsed = JSON.parse(avaliacaoData?.respostas ?? '{}');
            setQuestoes(questoesParsed);
            setRespostasUsuario(respostasParsed);
        }

        if (tipo === "avaliacao") {
            const avaliacaoData = data;
            
            // Parse das questões que agora incluem alternativas
            let questoesParsed = {};
            try {
                questoesParsed = JSON.parse(avaliacaoData?.questoes ?? '{}');
            } catch (e) {
                // Erro silencioso - dados podem não estar disponíveis ainda
            }
            
            let respostasParsed = {};
            try {
                respostasParsed = JSON.parse(avaliacaoData?.respostas ?? '{}');
            } catch (e) {
                // Erro silencioso - dados podem não estar disponíveis ainda
            }
            
            setQuestoes(questoesParsed);
            setRespostasUsuario(respostasParsed);
        }

        if (tipo === 'avaliacao_normal_visualizacao' && progressoAvaliacaoNormal) {
            const avaliacaoData = progressoAvaliacaoNormal;
            setQuestoes(avaliacaoData ?? '{}');
        }

        if (tipo === 'avaliacao_completa_visualizacao' && progressoAvaliacaoNormal) {
            const avaliacaoData = progressoAvaliacaoNormal;
            setQuestoes(avaliacaoData ?? '{}');
        }

    }, [tipo, progressoAvaliacaoExtra, progressoAvaliacaoNormal, data]);

    const displayData = tipo === 'avaliacao_extra' ? progressoAvaliacaoExtra : data;
    
    // Processar dados diretamente no render para garantir que estejam disponíveis
    let questoesParaRenderizar = questoes;
    let respostasParaRenderizar = respostasUsuario;
    
    // Se tipo é avaliacao e ainda não processou, processar agora
    if (tipo === "avaliacao" && data && Object.keys(questoes).length === 0 && data.questoes) {
        try {
            questoesParaRenderizar = JSON.parse(data.questoes ?? '{}');
            respostasParaRenderizar = JSON.parse(data.respostas ?? '{}');
        } catch (e) {
            // Erro silencioso - dados podem não estar disponíveis ainda
        }
    }
    
    // Verificar se há questões antes de renderizar
    const temQuestoesParaRenderizar = Object.keys(questoesParaRenderizar).length > 0;

    // Se for tipo avaliacao e dados não estão prontos, mostrar página vazia ou loading
    if (tipo === "avaliacao" && !temQuestoesParaRenderizar && data && !data.questoes) {
        return (
            <Document>
                <Page size="A4" style={styles.page}>
                    <Text style={styles.title}>Carregando avaliação...</Text>
                    <Text style={styles.subTitle}>{titulo}</Text>
                </Page>
            </Document>
        );
    }

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {(tipo === "avaliacao_extra" || tipo === "avaliacao") &&
                    <Text style={styles.title}>Avaliação de {displayData?.nomeUsuario || 'Aluno'}</Text>
                }
                <Text style={styles.subTitle}>{titulo}</Text>

                {tipo === "avaliacao_normal_visualizacao" &&
                    Object.keys(questoes).map((key, index) => {
                        const questao = questoes[key];
                        const alternativas = JSON.parse(questao.alternativas);
                        return (
                            <View key={index} style={styles.questionContainer}>
                                <Text style={styles.questionLabel}>
                                    {index + 1}. {questao.questao}
                                </Text>
                                {Array.isArray(alternativas) && alternativas.map((alternativa, altIndex) => {
                                    const alternativaLabel = String.fromCharCode(97 + altIndex); // 'a', 'b', 'c', etc.
                                    return (
                                        <View key={altIndex} style={styles.alternativeContainer}>
                                            <Text style={styles.alternativeLabel}>{alternativaLabel})</Text>
                                            <Text style={styles.alternativeText}>{alternativa}</Text>
                                        </View>
                                    );
                                })}
                            </View>
                        );
                    })
                }

                {tipo === "avaliacao_completa_visualizacao" &&
                    Object.keys(questoes).map((key, index) => {
                        const questao = questoes[key];
                        const alternativas = JSON.parse(questao.alternativas);
                        return (
                            <View key={index} style={styles.questionContainer}>
                                <Text style={styles.questionLabel}>
                                    {index + 1}. {questao.questao}
                                </Text>
                                {Array.isArray(alternativas) && alternativas.map((alternativa, altIndex) => {
                                    const alternativaLabel = String.fromCharCode(97 + altIndex); // 'a', 'b', 'c', etc.
                                    const isCorrect = (altIndex + 1) === parseInt(questao.resposta);
                                    return (
                                        <View key={altIndex} style={styles.alternativeContainer}>
                                            <Text style={styles.alternativeLabel}>{alternativaLabel})</Text>
                                            <Text style={[
                                                styles.alternativeText,
                                                isCorrect && { color: 'green' }
                                            ]}>
                                                {alternativa}
                                            </Text>
                                        </View>
                                    );
                                })}
                            </View>
                        );
                    })
                }

                {(tipo === "avaliacao_extra" || tipo === "avaliacao") && temQuestoesParaRenderizar &&

                    Object.keys(questoesParaRenderizar).map((key, index) => {
                        const questao = questoesParaRenderizar[key];
                        const respostaUsuario = respostasParaRenderizar[key];
                        
                        // Verificar se a questão existe e tem dados válidos
                        if (!questao || !questao.questao) {
                            return null;
                        }

                        // Parse das alternativas se existirem
                        let alternativasArray = [];
                        try {
                            if (questao.alternativas) {
                                alternativasArray = typeof questao.alternativas === 'string' 
                                    ? JSON.parse(questao.alternativas) 
                                    : questao.alternativas;
                            }
                        } catch (e) {
                            // Erro silencioso - alternativas podem não estar disponíveis
                        }

                        return (
                            <View key={key} style={styles.questionContainer}>
                                <Text style={styles.questionLabel}>
                                    {index + 1}. {questao.questao || 'Questão sem texto'}
                                </Text>
                                
                                {/* Mostrar todas as alternativas */}
                                {alternativasArray.length > 0 && (
                                    <View style={{ marginTop: 10 }}>
                                        {alternativasArray.map((alternativa, altIndex) => {
                                            const alternativaLabel = String.fromCharCode(97 + altIndex); // 'a', 'b', 'c', etc.
                                            const isSelected = respostaUsuario && (altIndex + 1) === parseInt(respostaUsuario);
                                            
                                            return (
                                                <View key={altIndex} style={styles.alternativeContainer}>
                                                    <Text style={styles.alternativeLabel}>{alternativaLabel})</Text>
                                                    <Text style={[
                                                        styles.alternativeText,
                                                        isSelected && styles.correctAnswer,
                                                        isSelected && { fontWeight: 'bold' }
                                                    ]}>
                                                        {alternativa}
                                                    </Text>
                                                </View>
                                            );
                                        })}
                                    </View>
                                )}
                            </View>
                        );
                    })

                }

                {(tipo === "avaliacao_extra" || tipo === "avaliacao") && (
                    <>
                        {!temQuestoesParaRenderizar && (
                            <Text style={{ marginTop: 20, color: 'red' }}>
                                Nenhuma questão encontrada. Verifique os logs do console.
                            </Text>
                        )}
                        <Text style={{ marginTop: 20, fontSize: 14, fontWeight: 'bold' }}>
                            Total de Acertos: {data?.acerto || 0} de {Object.keys(questoesParaRenderizar).length}
                        </Text>
                    </>
                )}
            </Page>
        </Document>
    );
};

export default GerarPDF;